import { Marker, Pane, Polyline } from 'react-leaflet'
import { useRoutePlanning } from 'providers/RoutePlanningProvider'
import { Box, Stack, Typography } from '@mui/material'
import { LatLngExpression } from 'leaflet'
import { MarkerInfo } from 'types/api/delivery'
import { Fragment } from 'react'
import {
  HoverAndSelectedTriangle,
  HoverTriangle,
  MapTooltip,
  MapTooltipWrapper,
  RoundMapTooltip,
  StyledTooltipBox,
} from './elements'
import { RouteMarkerContent } from './RouteMarkerContent'

type Props = {
  orderMarkers: MarkerInfo[]
  latlng: string
  oneShipmentSelected: boolean
}

export const RouteMarker = ({
  orderMarkers,
  latlng,
  oneShipmentSelected,
}: Props) => {
  const lat = latlng.split(',')[0]
  const long = latlng.split(',')[1]

  const { markerHovered, selectedOrders } = useRoutePlanning()

  const hoveredOrder = orderMarkers.find(
    (order) => order.orderId === markerHovered?.id
  )
  const firstSelectedOrder = orderMarkers.find((order) =>
    selectedOrders.map((selected) => selected.orderId).includes(order.orderId)
  )

  const getTime = (() => {
    if (hoveredOrder) {
      return hoveredOrder.time
    }
    if (firstSelectedOrder) {
      return firstSelectedOrder.time
    }
    return orderMarkers[0].time
  })()

  return (
    <Marker
      position={[Number(lat), Number(long)]}
      key={latlng}
      zIndexOffset={1000}>
      {!oneShipmentSelected && (
        <Pane name={`routemarker${latlng}`}>
          <MapTooltip direction='top' offset={[-14, 25]} opacity={1} permanent>
            <StyledTooltipBox
              hovered={Boolean(hoveredOrder)}
              selected={Boolean(firstSelectedOrder)}>
              {(hoveredOrder || firstSelectedOrder) && (
                <HoverTriangle
                  key={firstSelectedOrder?.orderId ?? hoveredOrder?.orderId}
                  firstSelectedOrder={Boolean(firstSelectedOrder)}
                />
              )}
              {hoveredOrder && firstSelectedOrder && (
                <HoverAndSelectedTriangle
                  key={`border-${
                    firstSelectedOrder?.orderId ?? hoveredOrder?.orderId
                  }`}
                />
              )}
              <MapTooltipWrapper>
                <Stack
                  justifyContent='center'
                  direction='row'
                  sx={{ position: 'absolute', top: '-24px' }}>
                  {orderMarkers.map((order) => (
                    <RouteMarkerContent
                      order={order}
                      latlng={latlng}
                      key={order.orderId}
                    />
                  ))}
                </Stack>
                <Typography variant='body2'>{getTime}</Typography>
              </MapTooltipWrapper>
            </StyledTooltipBox>
          </MapTooltip>
        </Pane>
      )}
      {oneShipmentSelected && orderMarkers[0].routeIsInPlanning && (
        <RoundMapTooltip
          key={orderMarkers[0].orderId}
          color={orderMarkers[0].color}
          direction='top'
          offset={[-14, 25]}
          opacity={1}
          permanent>
          <Stack direction='row'>
            {orderMarkers.map((order) => (
              <Fragment key={order.orderId}>
                <Box sx={{ padding: '0 2px' }}>
                  <Typography variant='body2'>
                    {order.routeNameIndex}
                  </Typography>
                </Box>
                {order.shipmentData.map((shipment) => {
                  return shipment.routePolygons?.map((position, i) => (
                    <Polyline
                      // eslint-disable-next-line react/no-array-index-key
                      key={order.orderId + position.toString() + i}
                      color={order.color}
                      positions={position as LatLngExpression[]}
                    />
                  ))
                })}
              </Fragment>
            ))}
          </Stack>
        </RoundMapTooltip>
      )}
    </Marker>
  )
}
